import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const About = () => {
  useEffect(() => {
    document.title = "Musa Yusuf Magoban | SOC Analyst & Cybersecurity Professional";
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { number: "8+", label: "Active Certifications", icon: "Award" },
    { number: "6", label: "Hands-On Projects", icon: "FolderOpen" },
    { number: "3", label: "Professional Roles", icon: "Briefcase" },
    { number: "2+", label: "Years Experience", icon: "Clock" }
  ];

  const journey = [
    {
      year: "2021-2023",
      title: "Engineering Foundation",
      description: "Metallurgical Engineering background developing analytical thinking and systematic problem-solving skills",
      icon: "Settings"
    },
    {
      year: "2023-2024", 
      title: "Cybersecurity Transition",
      description: "Professional certifications, virtual internships, and hands-on SOC lab development",
      icon: "Shield"
    },
    {
      year: "2024-Present",
      title: "Active Professional",
      description: "Training others, volunteer security work, and pursuing BSc in Cybersecurity in Germany",
      icon: "TrendingUp"
    }
  ];

  const technologies = [
    { name: "Graylog SIEM", category: "SOC Operations" },
    { name: "Wazuh", category: "SOC Operations" },
    { name: "Microsoft Sentinel", category: "SOC Operations" },
    { name: "ELK Stack", category: "SOC Operations" },
    { name: "Suricata IDS", category: "Network Security" },
    { name: "Microsoft Defender for Cloud", category: "Cloud Security" },
    { name: "Microsoft 365 Defender", category: "Threat Protection" },
    { name: "FlareVM", category: "Malware Analysis" },
    { name: "Python", category: "Automation" },
    { name: "LimaCharlie", category: "EDR/SOAR" },
    { name: "Tines", category: "SOAR" },
    { name: "Volatility", category: "Digital Forensics" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Musa Yusuf Magoban | SOC Analyst & Cybersecurity Professional</title>
        <meta 
          name="description" 
          content="SOC Analyst with expertise in threat detection, malware analysis, and AI-driven security automation. Currently pursuing BSc in Cybersecurity." 
        />
        <meta name="keywords" content="SOC analyst, cybersecurity, threat detection, malware analysis, security automation" />
      </Helmet>

      <Header />

      <main className="pt-16">
        {/* Enhanced Hero Section */}
        <section className="py-20 lg:py-32 bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Column - Content */}
              <div className="space-y-8">
                <div className="space-y-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-accent rounded-full pulse-security"></div>
                    <span className="text-secondary font-medium text-sm tracking-wide uppercase">
                      Active SOC Professional
                    </span>
                  </div>
                  
                  <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
                    <span className="block">Musa Yusuf</span>
                    <span className="block text-primary">Magoban</span>
                  </h1>
                  
                  <p className="text-xl text-primary font-medium">
                    SOC Analyst & Cybersecurity Professional
                  </p>
                  
                  <div className="space-y-4 text-lg text-text-secondary leading-relaxed">
                    <p>
                      Passionate cybersecurity professional with hands-on experience in <strong>security operations</strong>, 
                      <strong> threat detection</strong>, <strong>malware analysis</strong>, and <strong>AI-driven security automation</strong>.
                    </p>
                    
                    <p>
                      Transitioning from <span className="text-foreground font-medium">Metallurgical Engineering</span> to 
                      cybersecurity excellence with a unique analytical advantage. Currently pursuing a 
                      <span className="text-foreground font-medium"> BSc in Cybersecurity</span> at the International 
                      University of Applied Sciences, Germany.
                    </p>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Eye"
                    iconPosition="left"
                    className="cyber-gradient"
                    onClick={() => window.location.href = '/projects'}
                  >
                    View My Projects
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="Download"
                    iconPosition="left"
                    onClick={() => window.location.href = '/experience'}
                  >
                    View Experience
                  </Button>
                </div>

                {/* Quick Skills Tags */}
                <div className="flex flex-wrap gap-3">
                  {['SOC Operations', 'Threat Detection', 'Digital Forensics', 'Security Automation', 'Malware Analysis'].map((skill) => (
                    <span
                      key={skill}
                      className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full border border-primary/20"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              {/* Right Column - Stats & Visual */}
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4">
                  {stats.map((stat, index) => (
                    <div key={index} className="bg-surface rounded-lg p-4 security-shadow text-center">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-2">
                        <Icon name={stat.icon} size={20} className="text-primary" />
                      </div>
                      <div className="text-2xl font-bold text-foreground">{stat.number}</div>
                      <div className="text-sm text-text-secondary">{stat.label}</div>
                    </div>
                  ))}
                </div>

                {/* Current Status Card */}
                <div className="bg-surface rounded-lg p-6 security-shadow">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                      <Icon name="GraduationCap" size={24} className="text-accent" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">Currently Studying</h3>
                      <p className="text-sm text-text-secondary">BSc Cybersecurity, Germany</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <Icon name="Target" size={16} className="text-secondary mr-2" />
                      <span className="text-text-secondary">Specializing in AI Security & SOC Operations</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Journey Timeline */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Professional Journey
              </h2>
              <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                From engineering precision to cybersecurity excellence - a unique analytical advantage
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {journey.map((phase, index) => (
                <div key={index} className="relative">
                  <div className="bg-surface rounded-lg p-6 security-shadow hover:security-shadow-strong transition-security">
                    <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                      <Icon name={phase.icon} size={24} className="text-secondary" />
                    </div>
                    <div className="text-sm font-medium text-secondary mb-2">{phase.year}</div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">{phase.title}</h3>
                    <p className="text-text-secondary leading-relaxed">{phase.description}</p>
                  </div>
                  {/* Connection line */}
                  {index < journey.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-secondary to-transparent"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technology Showcase */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Technology Arsenal
              </h2>
              <p className="text-lg text-text-secondary">
                Hands-on experience with industry-standard cybersecurity tools and platforms
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {technologies.map((tech, index) => (
                <div key={index} className="bg-surface rounded-lg p-4 security-shadow text-center hover:security-shadow-strong transition-security">
                  <div className="font-medium text-foreground text-sm mb-1">{tech.name}</div>
                  <div className="text-xs text-secondary">{tech.category}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Goals & Vision */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Mission & Vision
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-surface rounded-lg p-6 security-shadow">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="Target" size={24} className="text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Current Focus</h3>
                <p className="text-text-secondary leading-relaxed">
                  Building expertise in SOC operations, threat detection, and security automation 
                  while contributing to cybersecurity education and community protection through 
                  volunteer work and mentorship.
                </p>
              </div>

              <div className="bg-surface rounded-lg p-6 security-shadow">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="Shield" size={24} className="text-accent" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Career Goals</h3>
                <p className="text-text-secondary leading-relaxed">
                  Contribute to building safer digital environments in critical sectors like 
                  healthcare, IoT, finance, and banking. Specializing in AI-driven security 
                  solutions and advanced threat detection.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-gradient-to-br from-primary to-secondary">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="space-y-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-white">
                Ready to Secure Your Organization?
              </h2>
              <p className="text-xl text-white/90 max-w-2xl mx-auto">
                Let's discuss how my cybersecurity expertise and analytical mindset 
                can strengthen your security operations and protect your digital assets.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="secondary"
                  size="lg"
                  iconName="Mail"
                  iconPosition="left"
                  className="bg-white/10 text-white border border-white/20 hover:bg-white/20 backdrop-blur-sm"
                  onClick={() => window.location.href = '/contact'}
                >
                  Get In Touch
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Download"
                  iconPosition="left"
                  className="border-white/30 text-white hover:bg-white/10"
                  onClick={() => window.location.href = '/certifications'}
                >
                  View Credentials
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default About; 